import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import Reveal from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";
import { jurisdictionPages } from "@/data/jurisdictionPages";

export default function JurisdictionPage({ slug }) {
  const page = jurisdictionPages[slug];
  useSEO({ title: page.metaTitle, description: page.metaDescription });
  useEffect(() => window.scrollTo(0, 0), [slug]);

  return (
    <div className="bg-paper">
      <Navbar />
      <section data-testid="pillar-hero" className="relative bg-brand">
        <img src={page.img} alt={page.h1} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-brand/85" />
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 pt-36 pb-20">
          <Link to="/" data-testid="pillar-breadcrumb" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400 hover:text-brand-500 transition-colors mb-8">
            <ArrowLeft className="w-3.5 h-3.5" /> Aurelius Family Office
          </Link>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-500 mb-4">Jurisdiction Guide · {page.name}</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight max-w-3xl">{page.h1}</h1>
          <p className="mt-6 text-base md:text-lg text-slate-300 max-w-2xl leading-relaxed">{page.intro}</p>
          <div className="mt-10 flex flex-wrap gap-10">
            {page.stats.map(([v, l]) => (
              <div key={l}>
                <p className="font-serif-display text-3xl text-white">{v}</p>
                <p className="text-xs uppercase tracking-widest text-slate-400 mt-1">{l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-4">Why {page.name}</p>
            <h2 className="font-outfit text-3xl md:text-4xl font-medium tracking-tight text-slate-950 mb-14">Key advantages for family offices</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 border-t border-s border-slate-200">
            {page.benefits.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.08}>
                <div data-testid={`pillar-benefit-${i}`} className="border-b border-e border-slate-200 p-8 lg:p-10 h-full hover:bg-slate-50 transition-colors">
                  <h3 className="font-outfit text-xl font-medium text-slate-950 mb-3">{b.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{b.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20 md:py-28">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-4">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-4">Regulatory Framework</p>
            <h2 className="font-outfit text-3xl md:text-4xl font-medium tracking-tight text-slate-950">How it is regulated</h2>
          </Reveal>
          <Reveal className="md:col-span-8 space-y-6" delay={0.1}>
            {page.regulatory.map((p, i) => (
              <p key={i} className="text-base text-slate-600 leading-relaxed">{p}</p>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-4">Costs</p>
            <h2 className="font-outfit text-3xl md:text-4xl font-medium tracking-tight text-slate-950">What it costs</h2>
            <p className="mt-6 text-base text-slate-600 leading-relaxed">Indicative figures, every mandate is priced against structure complexity and jurisdictional scope.</p>
          </Reveal>
          <Reveal className="md:col-span-7" delay={0.1}>
            <table data-testid="pillar-costs-table" className="w-full border border-slate-200 text-sm">
              <tbody>
                {page.costs.map(([item, cost]) => (
                  <tr key={item} className="border-b border-slate-200 last:border-0">
                    <td className="p-4 text-slate-600">{item}</td>
                    <td className="p-4 text-slate-950 font-medium text-end whitespace-nowrap">{cost}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Reveal>
        </div>
      </section>

      <section className="bg-slate-50 py-20 md:py-28">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-4">Timeline</p>
            <h2 className="font-outfit text-3xl md:text-4xl font-medium tracking-tight text-slate-950 mb-14">Setup timeline</h2>
          </Reveal>
          <div className="border-s border-slate-200">
            {page.timeline.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div data-testid={`pillar-timeline-${i}`} className="relative ps-10 pb-12 last:pb-0">
                  <span className="absolute -start-[5px] top-1.5 w-[9px] h-[9px] bg-brand-600" />
                  <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                    <h3 className="font-outfit text-xl font-medium text-slate-950">{s.title}</h3>
                    <span className="text-xs uppercase tracking-widest text-brand-700">{s.time}</span>
                  </div>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed max-w-xl">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-4">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-4">FAQ</p>
            <h2 className="font-outfit text-3xl md:text-4xl font-medium tracking-tight text-slate-950">{page.name} questions</h2>
          </Reveal>
          <div className="md:col-span-8">
            <Accordion type="single" collapsible className="w-full">
              {page.faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-slate-200">
                  <AccordionTrigger data-testid={`pillar-faq-trigger-${i}`} className="text-start font-outfit text-base md:text-lg font-medium text-slate-950 hover:text-brand-700 hover:no-underline py-6">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm md:text-base text-slate-600 leading-relaxed pb-6">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section className="bg-brand py-20 md:py-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <Reveal>
            <h2 className="font-serif-display text-3xl md:text-4xl font-light text-white">Discuss your {page.name} structure.</h2>
            <p className="mt-3 text-slate-400">Confidential, no-obligation conversation with a senior partner.</p>
          </Reveal>
          <Link
            to="/#consultation"
            data-testid="pillar-cta-button"
            className="bg-white hover:bg-brand-50 transition-colors text-brand px-8 py-4 text-sm tracking-widest uppercase rounded-none shrink-0"
          >
            Request Consultation
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
